import { useQueryClient } from '@tanstack/react-query';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import type { ContactsPagingResult } from '@/modules/contacts/types/contacts.types';
import type { ContactsRestFilters } from '@/modules/contacts/utils/contacts-rest-filter.utils';
import {
  addContactsToLists,
  addListMembers,
  createList,
  deleteList,
  getListById,
  getListContacts,
  getListMembershipJob,
  getLists,
  removeListMembers,
  startAddAllListMembersJob,
  syncContactListMembership,
  updateList,
} from '../services/lists.service';
import type {
  CampaignList,
  CreateListRequest,
  GetListsParams,
  ListMembersMutationResult,
  ListMutationResult,
  ListsPagingResult,
  StartListMembershipJobParams,
  UpdateListRequest,
} from '../types/lists.types';
import type { ListMembershipJobApi } from '../types/lists-api.types';

const invalidateListQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['lists'] });
  queryClient.invalidateQueries({ queryKey: ['list'] });
  queryClient.invalidateQueries({ queryKey: ['list-contacts'] });
  queryClient.invalidateQueries({ queryKey: ['contacts'] });
};

export const useGetLists = (params: GetListsParams = {}, options: { enabled?: boolean } = {}) => {
  return useGlobalQuery<ListsPagingResult>({
    queryKey: ['lists', params],
    queryFn: () => getLists(params),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: options.enabled ?? true,
  });
};

export const useGetListById = (listId: string, options: { enabled?: boolean } = {}) => {
  return useGlobalQuery<CampaignList>({
    queryKey: ['list', listId],
    queryFn: () => getListById(listId),
    staleTime: 30 * 1000,
    enabled: (options.enabled ?? true) && Boolean(listId),
  });
};

export const useGetListContacts = (
  params: {
    listId: string;
    pageNo?: number;
    pageSize?: number;
    q?: string;
    filter?: ContactsRestFilters;
  },
  options: { enabled?: boolean } = {}
) => {
  return useGlobalQuery<ContactsPagingResult>({
    queryKey: ['list-contacts', params],
    queryFn: () => getListContacts(params),
    staleTime: 15 * 1000,
    enabled: (options.enabled ?? true) && Boolean(params.listId),
  });
};

export const useCreateList = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<ListMutationResult, Error, CreateListRequest>({
    mutationFn: createList,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useUpdateList = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    ListMutationResult,
    Error,
    { listId: string; input: UpdateListRequest }
  >({
    mutationFn: updateList,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useDeleteList = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<ListMutationResult, Error, string>({
    mutationFn: deleteList,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useAddListMembers = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    ListMembersMutationResult,
    Error,
    { listId: string; contactIds: string[] }
  >({
    mutationFn: addListMembers,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useRemoveListMembers = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    ListMembersMutationResult,
    Error,
    { listId: string; contactIds: string[] }
  >({
    mutationFn: removeListMembers,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useAddContactsToLists = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    ListMembersMutationResult[],
    Error,
    { listIds: string[]; contactIds: string[] }
  >({
    mutationFn: addContactsToLists,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

export const useSyncContactListMembership = () => {
  const queryClient = useQueryClient();

  return useGlobalMutation<
    void,
    Error,
    { contactId: string; selectedListIds: string[]; currentListIds: string[] }
  >({
    mutationFn: syncContactListMembership,
    suppressToast: true,
    onSuccess: () => {
      invalidateListQueries(queryClient);
    },
  });
};

/** Queues a "select all" add-to-lists job. Nothing is invalidated yet — the job has not run. */
export const useStartAddAllListMembersJob = () =>
  useGlobalMutation<ListMembershipJobApi, Error, StartListMembershipJobParams>({
    mutationFn: startAddAllListMembersJob,
    suppressToast: true,
  });

const LIST_MEMBERSHIP_JOB_POLL_MS = 2000;

export const isListMembershipJobLive = (job: Pick<ListMembershipJobApi, 'status'> | undefined): boolean =>
  job?.status === 'queued' || job?.status === 'processing';

/**
 * Polls a select-all job every 2 s while it is queued or processing, then stops. When it finishes the
 * list and contact reads are invalidated once, since memberships and counts have changed.
 */
export const useListMembershipJob = (jobId: string | null) => {
  const queryClient = useQueryClient();

  return useGlobalQuery<ListMembershipJobApi>({
    queryKey: ['list-membership-job', jobId],
    queryFn: async () => {
      const job = await getListMembershipJob(jobId ?? '');
      if (!isListMembershipJobLive(job)) {
        invalidateListQueries(queryClient);
      }
      return job;
    },
    enabled: Boolean(jobId),
    refetchInterval: (query) => (isListMembershipJobLive(query.state.data) ? LIST_MEMBERSHIP_JOB_POLL_MS : false),
    staleTime: 0,
  });
};
